import { FeatureFlag } from '@ps/web-core-enums';
import { isNil } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';

import useFeature from '@swf/core/hooks/useFeature';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { EventId } from '@swf/generated/types/api/sports';
import type { EventTypeId } from '@swf/sports/types/eventType';
import { isFootballScoreboard, isTennisScoreboard } from '@swf/sports/utils/isScoreboard';
import { isScoreboardSupportedEventType } from '@swf/sports/utils/isScoreboardSupportedEventType';

import type { EventStatus } from '../EventStatus/hooks/useEventStatus';

import DefaultEventCard from './components/DefaultEventCard/DefaultEventCard';
import Football from './components/Football/Football';
import Loading from './components/Loading/Loading';
import Tennis from './components/Tennis/Tennis';
import Unavailable from './components/Unavailable/Unavailable';

export interface Props {
  eventId: EventId;
  eventTypeId: EventTypeId;
  eventTime: string;
  inPlay?: boolean;
  name: string;
  eventStatusOverride?: EventStatus;
  canTurnInPlay?: boolean;
}

const EventCard: React.FC<Props> = ({
  eventTypeId,
  eventId,
  name,
  eventTime,
  inPlay,
  eventStatusOverride,
  canTurnInPlay
}) => {
  const eventScoreboard = useSelector((rootState: RootState) =>
    select.scoreboards.eventScoreboard(rootState, eventId)
  );
  const isScoreboardsEnabled = useFeature(FeatureFlag.ISPScoreboards);
  const hasScoreboardsNetworkError = useSelector(select.scoreboards.hasNetworkError);
  let scoreboard = null;

  if (
    inPlay &&
    isScoreboardSupportedEventType(eventTypeId) &&
    isScoreboardsEnabled &&
    !hasScoreboardsNetworkError
  ) {
    if (isNil(eventScoreboard?.data) && !eventScoreboard?.hasError) {
      scoreboard = (
        <Loading
          eventId={eventId}
          eventTime={eventTime}
          inPlay={inPlay}
          name={name}
          eventTypeId={eventTypeId}
        />
      );
    }

    if (!eventScoreboard || eventScoreboard.hasError) {
      scoreboard = (
        <Unavailable
          name={name}
          eventTime={eventTime}
          eventTypeId={eventTypeId}
          eventId={eventId}
        />
      );
    }

    if (isTennisScoreboard(eventScoreboard)) {
      scoreboard = (
        <Tennis
          eventId={eventId}
          eventTime={eventTime}
          name={name}
          tennisMatch={eventScoreboard.data}
        />
      );
    }

    if (isFootballScoreboard(eventScoreboard)) {
      scoreboard = (
        <Football
          eventTime={eventTime}
          eventId={eventId}
          name={name}
          footballMatch={eventScoreboard.data}
        />
      );
    }
  }

  return (
    <>
      <DefaultEventCard
        title={name}
        canTurnInPlay={canTurnInPlay}
        inPlay={inPlay}
        eventTime={eventTime}
        eventStatusOverride={eventStatusOverride}
        titleOnly={Boolean(scoreboard)}
      />
      {scoreboard}
    </>
  );
};

export { EventCard };
